"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.EditPreview = void 0;
var _react = require("react");
var _cropUtils = require("../utils/cropUtils");
var _jsxRuntime = require("react/jsx-runtime");
const cropResizeHandles = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const EditPreview = ({
  crop,
  cropHasChanges,
  error,
  image,
  isCropping,
  onClose,
  onConfirmCrop,
  onCropChange,
  onDiscardCrop,
  onSourceAspectChange,
  previewStyle,
  sourceAspect
}) => {
  const frameRef = (0, _react.useRef)(null);
  const [dragging, setDragging] = (0, _react.useState)(false);
  const [resizeDrag, setResizeDrag] = (0, _react.useState)(null);
  const cropBox = (0, _cropUtils.getCropBox)(crop, sourceAspect);
  const frameStyle = isCropping ? {
    aspectRatio: sourceAspect,
    width: `min(760px, 100%, calc(50dvh * ${sourceAspect}))`
  } : (0, _cropUtils.getCroppedPreviewFrameStyle)(crop, sourceAspect);
  const imageStyle = isCropping ? previewStyle : (0, _cropUtils.getCroppedPreviewImageStyle)(crop, previewStyle, sourceAspect);
  const moveCrop = (clientX, clientY) => {
    const frame = frameRef.current;
    if (!frame) {
      return;
    }
    const rect = frame.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width * 100;
    const y = (clientY - rect.top) / rect.height * 100;
    onCropChange('x', x);
    onCropChange('y', y);
  };
  const handlePointerDown = event => {
    if (!isCropping) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    moveCrop(event.clientX, event.clientY);
  };
  const handlePointerMove = event => {
    if (!dragging || resizeDrag) {
      return;
    }
    moveCrop(event.clientX, event.clientY);
  };
  const handlePointerUp = event => {
    releasePointer(event);
    setDragging(false);
  };
  const handleResizePointerDown = (event, handle) => {
    if (!isCropping) {
      return;
    }
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(false);
    setResizeDrag({
      handle,
      crop
    });
  };
  const handleResizePointerMove = event => {
    if (!resizeDrag) {
      return;
    }
    const frame = frameRef.current;
    if (!frame) {
      return;
    }
    const rect = frame.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width * 100;
    const y = (event.clientY - rect.top) / rect.height * 100;
    onCropChange((0, _cropUtils.getCropFromResizeHandle)(resizeDrag.crop, resizeDrag.handle, x, y, sourceAspect));
  };
  const handleResizePointerUp = event => {
    releasePointer(event);
    setResizeDrag(null);
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    className: "editor-preview",
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
      className: "btn btn-light editor-close",
      type: "button",
      onClick: onClose,
      "aria-label": "Close editor",
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("i", {
        className: "bi bi-x-lg"
      })
    }), isCropping && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: cropHasChanges ? 'crop-preview-actions is-visible' : 'crop-preview-actions',
      role: "group",
      "aria-hidden": !cropHasChanges,
      "aria-label": "Crop actions",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("strong", {
        className: "crop-preview-actions-label",
        children: "Unsaved changes"
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        className: "crop-preview-action-buttons",
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
          className: "btn bg-green editor-icon-button",
          type: "button",
          onClick: onConfirmCrop,
          tabIndex: cropHasChanges ? 0 : -1,
          "aria-label": "Confirm crop",
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)("i", {
            className: "bi bi-check-lg"
          })
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
          className: "btn bg-beige editor-icon-button",
          type: "button",
          onClick: onDiscardCrop,
          tabIndex: cropHasChanges ? 0 : -1,
          "aria-label": "Discard crop changes",
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)("i", {
            className: "bi bi-x-lg"
          })
        })]
      })]
    }), error && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      className: "alert alert-danger editor-preview-error mb-3",
      children: error
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: isCropping ? 'edit-image-frame is-cropping' : 'edit-image-frame',
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      ref: frameRef,
      style: frameStyle,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
        src: image.previewUrl,
        alt: image.file.name,
        style: imageStyle,
        onLoad: event => onSourceAspectChange(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight)
      }), isCropping && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        className: "crop-box",
        style: (0, _cropUtils.getCropBoxStyle)(crop, sourceAspect),
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          className: "crop-box-grid"
        }), cropResizeHandles.map(handle => /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
          "aria-label": `Resize crop from ${handle}`,
          className: getHandleClassName(handle, cropBox),
          onPointerCancel: handleResizePointerUp,
          onPointerDown: event => handleResizePointerDown(event, handle),
          onPointerMove: handleResizePointerMove,
          onPointerUp: handleResizePointerUp,
          type: "button"
        }, handle))]
      })]
    })]
  });
};
exports.EditPreview = EditPreview;
function releasePointer(event) {
  if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId);
  }
}
function getHandleClassName(handle, box) {
  const classes = ['crop-resize-handle', handle];
  const reachesLeft = box.left <= 0.01;
  const reachesTop = box.top <= 0.01;
  const reachesRight = box.left + box.width >= 99.99;
  const reachesBottom = box.top + box.height >= 99.99;
  if (handle.includes('left') && reachesLeft) {
    classes.push('is-left-edge');
  }
  if (handle.includes('right') && reachesRight) {
    classes.push('is-right-edge');
  }
  if (handle.includes('top') && reachesTop) {
    classes.push('is-top-edge');
  }
  if (handle.includes('bottom') && reachesBottom) {
    classes.push('is-bottom-edge');
  }
  return classes.join(' ');
}